import { useState, useEffect, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { Ban, Cpu } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { ConfirmToggleDialog } from '@/components/shared/confirm-toggle-dialog';
import { usePermissions } from '@/hooks/use-permissions';
import {
	useBrandSettings,
	useUpdateAutomationThreshold,
	useUpdateBrandSettings,
	useSetAutomationGuards
} from '@/hooks/use-brand-settings';
import { HandoffByChannel } from './automation-guards';
import { ThresholdSlider } from './reply-thresholds';
import { DEFAULT_AUTOMATION_GUARDS, MAX_EMAIL_REPLY_DELAY_MINUTES } from '@/lib/automation-guards';
import { QualityCheckCard } from '@/modules/brand';
import {
	CommitNumberInput,
	Field,
	ReadOnlyNotice,
	SaveBar,
	SETTINGS_INPUT_CLASS,
	SETTINGS_TEXTAREA_CLASS,
	SettingsBody,
	SettingsCard,
	SettingsCardSkeleton,
	SettingsDivider,
	SettingsError,
	SettingsPage,
	SettingsSection,
	ToggleRow
} from '@/modules/settings';
import type { AutomationGuards, QualityJudgeMode } from '@/types';
import { useAccountFlags, useUpdateKillSwitch, useAiSettings, useUpdateAiSettings } from '@/hooks';
import { getApiErrorMessage } from '@/lib/utils';
import { buildDefaultAiSettings } from '@/lib/ai-settings-defaults';

const MIN_THRESHOLD = 0;
const MAX_THRESHOLD = 1;
const DEFAULT_AUTOMATION_THRESHOLD = 0.9;
/** Matches the backend's CONFIDENCE_THRESHOLD default, used when a brand has never set one. */
const DEFAULT_SHOW_DRAFT_THRESHOLD = 0.5;
/** What the backend tags with when the field is left empty (RESPONDED_TICKET_TAG). */
const DEFAULT_AUTOMATION_TAG = 'responded-by-kim-ai';
const MAX_AUTOMATION_TAG_LENGTH = 50;
const DEFAULT_AI_CAUTION_MESSAGE = 'This response was generated by AI.';
const MAX_AI_CAUTION_MESSAGE = 500;

const rangeError = (value: number) =>
	!Number.isFinite(value) || value < MIN_THRESHOLD || value > MAX_THRESHOLD ? `Must be between ${MIN_THRESHOLD} and ${MAX_THRESHOLD}` : undefined;

/** Optional: an empty field is valid and means the default tag. */
const automationTagError = (value: string): string | undefined =>
	value.trim().length > MAX_AUTOMATION_TAG_LENGTH ? `At most ${MAX_AUTOMATION_TAG_LENGTH} characters` : undefined;

/** Order-insensitive comparison, so re-ordering channels does not count as an edit. */
const canonicalGuards = (g: AutomationGuards): string =>
	JSON.stringify({
		...g,
		humanReplyExemptChannels: [...g.humanReplyExemptChannels].sort(),
		botReplyAllowance: Object.fromEntries(Object.entries(g.botReplyAllowance).sort())
	});

/** Everything the page saves, so one snapshot serves dirty-checking and Cancel. */
interface AutomationForm {
	automationThreshold: number;
	showDraftThreshold: number;
	automationTag: string;
	qualityJudgeMode: QualityJudgeMode;
	aiCautionEnabled: boolean;
	aiCautionMessage: string;
	guards: AutomationGuards;
}

const DEFAULT_FORM: AutomationForm = {
	automationThreshold: DEFAULT_AUTOMATION_THRESHOLD,
	showDraftThreshold: DEFAULT_SHOW_DRAFT_THRESHOLD,
	automationTag: '',
	qualityJudgeMode: 'strict',
	aiCautionEnabled: false,
	aiCautionMessage: DEFAULT_AI_CAUTION_MESSAGE,
	guards: DEFAULT_AUTOMATION_GUARDS
};

// ============================================================================
// Master switch — intent automation on/off for the whole account (the kill switch, inverted)
// ============================================================================

function AutomationMasterSwitch() {
	const { flags, isLoading } = useAccountFlags();
	const updateKillSwitch = useUpdateKillSwitch();
	const { can } = usePermissions();
	// The desired kill-switch value waiting for confirmation: true = turn automation off.
	const [pendingKillSwitch, setPendingKillSwitch] = useState<boolean | null>(null);
	const automationOn = !flags.intent_automation_kill_switch;

	const confirm = async () => {
		if (pendingKillSwitch === null) return;
		try {
			await updateKillSwitch.mutateAsync(pendingKillSwitch);
			toast.success('Automation setting updated');
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to update automation setting'));
		} finally {
			setPendingKillSwitch(null);
		}
	};

	return (
		<>
			<SettingsCard>
				<ToggleRow
					title='Automation'
					description='Lets Kim send replies on its own for the intents that have Auto-send on. Turn off to stop every automated reply.'
					checked={automationOn}
					disabled={isLoading || !can('edit', 'automation')}
					onCheckedChange={(nextOn) => setPendingKillSwitch(!nextOn)}
				/>
			</SettingsCard>
			<ConfirmToggleDialog
				pendingValue={pendingKillSwitch === null ? null : !pendingKillSwitch}
				icon={pendingKillSwitch ? Ban : Cpu}
				title={pendingKillSwitch ? 'Disable Automation?' : 'Enable Automation?'}
				description={
					pendingKillSwitch
						? 'Kim will stop handling tickets now. Existing tickets will stay tagged "Managed by Kim," but no new ones will be assigned. Your team will handle all incoming tickets from here on.'
						: 'Kim will resume handling tickets automatically. Per-intent automation toggles will become active again and new tickets will be assigned based on your intent settings.'
				}
				confirmLabel={pendingKillSwitch ? 'Disable Automation' : 'Enable Automation'}
				isPending={updateKillSwitch.isPending}
				onConfirm={() => void confirm()}
				onCancel={() => setPendingKillSwitch(null)}
			/>
		</>
	);
}

// ============================================================================
// Settings form — every section saves through one Save bar
// ============================================================================

function AutomationSettings({ canEdit, intro }: { canEdit: boolean; intro: ReactNode }) {
	const { data: brandData, isLoading: brandLoading, isError: brandError } = useBrandSettings();
	const { data: aiData, isLoading: aiLoading, isError: aiError } = useAiSettings();
	const updateThreshold = useUpdateAutomationThreshold();
	const updateBrand = useUpdateBrandSettings();
	const updateAi = useUpdateAiSettings();
	const setAutomationGuards = useSetAutomationGuards();

	const [form, setForm] = useState<AutomationForm>(DEFAULT_FORM);
	const [saved, setSaved] = useState<AutomationForm>(DEFAULT_FORM);

	// Each source fills its own fields, so whichever loads second does not undo the first.
	useEffect(() => {
		if (!brandData) return;
		const fromBrand = {
			automationThreshold: brandData.automationThreshold ?? DEFAULT_AUTOMATION_THRESHOLD,
			qualityJudgeMode: brandData.qualityJudgeMode ?? 'strict',
			aiCautionEnabled: brandData.aiCautionEnabled ?? false,
			aiCautionMessage: brandData.aiCautionMessage ?? DEFAULT_AI_CAUTION_MESSAGE,
			guards: brandData.automationGuards
		};
		setForm((prev) => ({ ...prev, ...fromBrand }));
		setSaved((prev) => ({ ...prev, ...fromBrand }));
	}, [brandData]);

	useEffect(() => {
		if (!aiData) return;
		// Empty when the brand has not set a tag: the placeholder shows the default it gets.
		const fromAi = { automationTag: aiData.automationTag ?? '', showDraftThreshold: aiData.confidenceThreshold ?? DEFAULT_SHOW_DRAFT_THRESHOLD };
		setForm((prev) => ({ ...prev, ...fromAi }));
		setSaved((prev) => ({ ...prev, ...fromAi }));
	}, [aiData]);

	const set = <K extends keyof AutomationForm>(key: K, value: AutomationForm[K]) => setForm((prev) => ({ ...prev, [key]: value }));
	const setGuards = (patch: Partial<AutomationGuards>) => setForm((prev) => ({ ...prev, guards: { ...prev.guards, ...patch } }));
	const { guards } = form;
	const emailDelay = guards.emailReplyDelay;

	const thresholdChanged = form.automationThreshold !== saved.automationThreshold;
	const brandChanged =
		form.qualityJudgeMode !== saved.qualityJudgeMode ||
		form.aiCautionEnabled !== saved.aiCautionEnabled ||
		form.aiCautionMessage !== saved.aiCautionMessage;
	const guardsChanged = canonicalGuards(guards) !== canonicalGuards(saved.guards);
	const aiChanged = form.showDraftThreshold !== saved.showDraftThreshold || form.automationTag.trim() !== saved.automationTag;
	const isDirty = thresholdChanged || brandChanged || guardsChanged || aiChanged;

	const autoSendError = rangeError(form.automationThreshold);
	const showDraftError = rangeError(form.showDraftThreshold);
	const tagError = automationTagError(form.automationTag);
	const isPending = updateThreshold.isPending || updateBrand.isPending || updateAi.isPending || setAutomationGuards.isPending;
	const disabled = !canEdit || isPending;

	const handleSave = async () => {
		const automationTag = form.automationTag.trim();
		try {
			// One write at a time: brand settings and guards both rewrite the brand's custom attributes, so parallel writes lose each other's change.
			if (thresholdChanged) await updateThreshold.mutateAsync({ automationThreshold: form.automationThreshold });
			if (brandChanged) {
				await updateBrand.mutateAsync({
					qualityJudgeMode: form.qualityJudgeMode,
					aiCautionEnabled: form.aiCautionEnabled,
					aiCautionMessage: form.aiCautionMessage
				});
			}
			// The hold message is edited on Handling, so it goes back exactly as the server has it.
			if (guardsChanged && brandData) {
				await setAutomationGuards.mutateAsync({ ...guards, holdMessage: brandData.automationGuards.holdMessage });
			}
			// ai_settings is saved whole, so the show-draft threshold and the tag go out in one write.
			if (aiData && aiChanged) {
				await updateAi.mutateAsync({ ...buildDefaultAiSettings(aiData), confidenceThreshold: form.showDraftThreshold, automationTag });
			}
			const next = { ...form, automationTag };
			setForm(next);
			setSaved(next);
			toast.success('Settings saved');
		} catch (err) {
			toast.error(getApiErrorMessage(err, 'Failed to save settings. Please try again.'));
		}
	};

	if (brandError || aiError || brandLoading || aiLoading) {
		return (
			<SettingsBody>
				{intro}
				{brandError || aiError ? (
					<SettingsError />
				) : (
					<SettingsSection>
						<SettingsCardSkeleton rows={4} />
						<SettingsCardSkeleton rows={3} />
					</SettingsSection>
				)}
			</SettingsBody>
		);
	}

	return (
		<>
			<SettingsBody>
				{intro}
				<SettingsSection label='Before Kim sends a reply'>
					<SettingsCard>
						<ToggleRow title='Handoff by channel' description='Choose when Kim stops replying on each channel so a person can take over.' />
						<HandoffByChannel guards={guards} onChange={setGuards} disabled={disabled} />
						<SettingsDivider />
						<ToggleRow
							title='Send on messages with multiple intents'
							description='When a customer asks about more than one thing in one message, Kim sends a single reply covering them. Off sends the reply to an agent as a draft.'
							checked={guards.multiIntentAutoSend}
							disabled={disabled}
							onCheckedChange={(multiIntentAutoSend) => setGuards({ multiIntentAutoSend })}
						/>
						<SettingsDivider />
						<ToggleRow
							title='Wait before sending'
							description="Email only. Hold Kim's reply for a random few minutes before it goes out, so a person can step in first. If the ticket changes during the wait, Kim does not send it."
							checked={emailDelay.enabled}
							disabled={disabled}
							onCheckedChange={(enabled) => setGuards({ emailReplyDelay: { ...emailDelay, enabled } })}
						/>
						{emailDelay.enabled && (
							<div className='flex items-center justify-between gap-4'>
								<p className='text-[13px] font-medium leading-[18px] text-ink'>Hold for</p>
								<div className='flex items-center gap-2 text-[13px] text-gray-900'>
									<CommitNumberInput
										aria-label='Shortest wait in minutes'
										value={emailDelay.minMinutes}
										min={0}
										max={MAX_EMAIL_REPLY_DELAY_MINUTES}
										disabled={disabled}
										// Moving a bound past the other drags the other along, so the range is never empty.
										onCommit={(minMinutes) =>
											setGuards({ emailReplyDelay: { ...emailDelay, minMinutes, maxMinutes: Math.max(minMinutes, emailDelay.maxMinutes) } })
										}
									/>
									to
									<CommitNumberInput
										aria-label='Longest wait in minutes'
										value={emailDelay.maxMinutes}
										min={0}
										max={MAX_EMAIL_REPLY_DELAY_MINUTES}
										disabled={disabled}
										onCommit={(maxMinutes) =>
											setGuards({ emailReplyDelay: { ...emailDelay, maxMinutes, minMinutes: Math.min(maxMinutes, emailDelay.minMinutes) } })
										}
									/>
									minutes
								</div>
							</div>
						)}
						{/* Hidden for now (product call); the saved value still goes back unchanged with every save.
						<SettingsDivider />
						<ToggleRow
							title='Keep sending when operating hours cannot be read'
							description='If your operating hours cannot be loaded for a moment, Kim carries on sending. Turn off to have Kim hold back until they can be read.'
							checked={guards.operatingHoursFailOpen}
							disabled={disabled}
							onCheckedChange={(operatingHoursFailOpen) => setGuards({ operatingHoursFailOpen })}
						/>
						*/}
					</SettingsCard>
				</SettingsSection>

				<SettingsSection label='Quality gate'>
					<SettingsCard>
						<ToggleRow
							title='Reply thresholds'
							description='How the score is built: checks on the finished reply, grounding (every claim traces to a tool result or source), completeness (the ask is answered), tone (brand voice) and relevancy (a second model confirms the reply answers the question), combined into one number from 0 to 1.'
						/>
						<div className='flex flex-col gap-3'>
							<ThresholdSlider
								label='Show response to sentinel'
								hint='Agents see a draft when the score is at or above this.'
								value={form.showDraftThreshold}
								onChange={(value) => set('showDraftThreshold', value)}
								error={showDraftError}
								disabled={disabled}
							/>
							<SettingsDivider />
							<ThresholdSlider
								label='Allow AI Auto Response'
								hint='Kim sends on its own from this score.'
								value={form.automationThreshold}
								onChange={(value) => set('automationThreshold', value)}
								error={autoSendError}
								disabled={disabled}
							/>
							{!autoSendError && !showDraftError && form.automationThreshold < form.showDraftThreshold && (
								<p className='rounded-[10px] bg-amber-50 px-3 py-2 text-[12px] leading-4 text-amber-800'>
									Auto-send is lower than the draft threshold, so Kim may send replies on its own that would not even have been shown
									to an agent as a draft.
								</p>
							)}
						</div>
						<SettingsDivider />
						<ToggleRow
							title='Hand over to a person below'
							description='Replies scoring under this are handed to a person instead of being drafted for an agent. It never sits above the auto-send threshold.'
							action={
								<CommitNumberInput
									aria-label='Hand-over threshold'
									value={guards.assistedThreshold}
									min={MIN_THRESHOLD}
									max={MAX_THRESHOLD}
									step={0.05}
									integer={false}
									disabled={disabled}
									onCommit={(assistedThreshold) => setGuards({ assistedThreshold })}
								/>
							}
						/>
						<SettingsDivider />
						<ToggleRow
							title='Final relevancy check'
							description='Before sending on its own, a second model checks the reply answers the question.'
							checked={guards.relevancyJudgeEnabled}
							disabled={disabled}
							onCheckedChange={(relevancyJudgeEnabled) => setGuards({ relevancyJudgeEnabled })}
						/>
						<SettingsDivider />
						<ToggleRow
							title='Check the reply matches the resolution'
							description='Kim compares the reply with the resolution it worked out and only sends when they agree. Turn off to let a reply it could not verify go out.'
							checked={guards.resolutionConsistencyCheck}
							disabled={disabled}
							onCheckedChange={(resolutionConsistencyCheck) => setGuards({ resolutionConsistencyCheck })}
						/>
					</SettingsCard>
				</SettingsSection>

				<SettingsSection label='Customer-facing message'>
					<SettingsCard className='gap-6'>
						<ToggleRow
							title='AI disclaimer message'
							description='Append a note to automated replies so customers know they were written by AI.'
							checked={form.aiCautionEnabled}
							disabled={disabled}
							onCheckedChange={(value) => set('aiCautionEnabled', value)}
						/>
						<Field label='Disclaimer' htmlFor='ai-disclaimer' hint="Added under Kim's reply, after a line, in italics.">
							<textarea
								id='ai-disclaimer'
								value={form.aiCautionMessage}
								onChange={(e) => set('aiCautionMessage', e.target.value)}
								disabled={disabled || !form.aiCautionEnabled}
								placeholder={DEFAULT_AI_CAUTION_MESSAGE}
								maxLength={MAX_AI_CAUTION_MESSAGE}
								className={SETTINGS_TEXTAREA_CLASS}
							/>
						</Field>
					</SettingsCard>
				</SettingsSection>

				<SettingsSection label='Advanced'>
					<SettingsCard>
						<ToggleRow
							title='Allow Kim to answer on intents outside your list'
							description='Let Kim auto-send when it classifies a ticket under an intent you have not set up. Off is safer.'
							checked={guards.novelIntentAutoSend}
							disabled={disabled}
							onCheckedChange={(novelIntentAutoSend) => setGuards({ novelIntentAutoSend })}
						/>
						<SettingsDivider />
						<ToggleRow
							title='Allow Kim to answer resolved tickets'
							description='Let Kim reply on a ticket that is already marked resolved.'
							checked={guards.autoSendOnResolvedTickets}
							disabled={disabled}
							onCheckedChange={(autoSendOnResolvedTickets) => setGuards({ autoSendOnResolvedTickets })}
						/>
						<SettingsDivider />
						<QualityCheckCard
							mode={form.qualityJudgeMode}
							onModeChange={(mode) => set('qualityJudgeMode', mode)}
							isLoading={false}
							isError={false}
							disabled={disabled}
						/>
						<SettingsDivider />
						<Field
							label='Automation tag'
							htmlFor='automation-tag'
							error={tagError}
							hint={`Added to a ticket when Kim sends an automated reply. Assisted replies are not tagged. Leave empty to use ${DEFAULT_AUTOMATION_TAG}. Helpdesk views filtered on the old tag stop matching new tickets after you change it.`}
						>
							<input
								id='automation-tag'
								type='text'
								value={form.automationTag}
								onChange={(e) => set('automationTag', e.target.value)}
								disabled={disabled}
								placeholder={DEFAULT_AUTOMATION_TAG}
								className={SETTINGS_INPUT_CLASS}
							/>
						</Field>
					</SettingsCard>
				</SettingsSection>
			</SettingsBody>

			<SaveBar
				visible={isDirty}
				saving={isPending}
				canSave={canEdit && !autoSendError && !showDraftError && !tagError}
				onCancel={() => setForm(saved)}
				onSave={() => void handleSave()}
			/>
		</>
	);
}

// ============================================================================
// Page
// ============================================================================

/** Settings › Automation: when Kim may send a reply on its own. */
export default function SettingsAutomationPage() {
	const { can } = usePermissions();
	// View-only roles can open the page; every control is locked and the backend refuses the writes anyway.
	const canEdit = can('edit', 'automation');
	const [searchParams] = useSearchParams();
	const linkedTab = searchParams.get('tab');

	// Old links: the Slack automation alert opens one log entry (`?tab=sent&entry=…`), and the
	// per-intent switches moved to Intents.
	if (linkedTab === 'sent') {
		const params = new URLSearchParams(searchParams);
		params.set('tab', 'automation');
		return <Navigate to={`/settings/logs?${params.toString()}`} replace />;
	}
	if (linkedTab === 'intents') return <Navigate to='/settings/intents?tab=active' replace />;

	return (
		<SettingsPage title='Automation'>
			<AutomationSettings
				canEdit={canEdit}
				intro={
					<>
						{!canEdit && <ReadOnlyNotice />}
						<SettingsSection>
							<AutomationMasterSwitch />
						</SettingsSection>
					</>
				}
			/>
		</SettingsPage>
	);
}
